// Approved projections (/v2/approved-services, /v2/approved-subservices) are
// read-only snapshots of the catalog. These helpers let callers keep speaking
// the /v2/services shape whichever endpoint the flag selects.

const TYPE_MAP: Record<string, string> = {
  "approved-services": "services",
  "approved-subservices": "subservices",
};

const RELATIONSHIP_MAP: Record<string, string> = {
  "approved-subservices": "subservices",
  "approved-service": "service",
};

const INCLUDE_SEGMENT_MAP: Record<string, string> = {
  subservices: "approved-subservices",
  service: "approved-service",
};

export const translateInclude = (include?: string[]): string[] | undefined =>
  include?.map((path) =>
    path
      .split(".")
      .map((segment) => INCLUDE_SEGMENT_MAP[segment] ?? segment)
      .join(".")
  );

const renameIdentifier = (identifier: any) =>
  identifier && TYPE_MAP[identifier.type]
    ? { ...identifier, type: TYPE_MAP[identifier.type] }
    : identifier;

const renameLinkage = (data: any) =>
  Array.isArray(data) ? data.map(renameIdentifier) : renameIdentifier(data);

const normalizeRelationships = (relationships: any) =>
  relationships
    ? Object.fromEntries(
        Object.entries(relationships).map(([key, rel]: [string, any]) => [
          RELATIONSHIP_MAP[key] ?? key,
          rel && "data" in rel
            ? { ...rel, data: renameLinkage(rel.data) }
            : rel,
        ])
      )
    : relationships;

const normalizeResource = (resource: any) => {
  const type = resource && TYPE_MAP[resource.type];
  if (!type) return resource;

  const { "user-defined-fields": userDefinedFields, ...attributes } =
    resource.attributes ?? {};

  return {
    ...resource,
    type,
    attributes:
      type === "services"
        ? { ...attributes, state: "active", variables: userDefinedFields ?? [] }
        : { ...attributes, state: "active", active: true },
    relationships: normalizeRelationships(resource.relationships),
  };
};

export const normalizeApproved = <T>(doc: T): T => {
  if (!doc) return doc;
  const { data, included } = doc as any;
  return {
    ...(doc as any),
    data: Array.isArray(data)
      ? data.map(normalizeResource)
      : normalizeResource(data),
    ...(included ? { included: included.map(normalizeResource) } : {}),
  };
};
